'use client'

import { ImgHTMLAttributes, useEffect, useRef, useState } from 'react'

const DEFAULT_PLACEHOLDER_SRC = 'https://vinc.gumlet.io/gallery/thumbnails/vi.jpg'
const DEFAULT_VIDEO_PLACEHOLDER_SRC =
  'https://vinc.gumlet.io/gallery/video-thumbnails/vi.jpg'

function isVideoThumbnailUrl(url: string) {
  return /video-thumbnails/i.test(url)
}

type ProductThumbnailProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> & {
  src?: string | null
  alt: string
  fallbackSrc?: string
}

export const ProductThumbnail = ({
  src,
  alt,
  fallbackSrc,
  onError,
  ...imgProps
}: ProductThumbnailProps) => {
  const normalizedSrc = src?.trim() || ''
  const defaultFallback = isVideoThumbnailUrl(normalizedSrc)
    ? DEFAULT_VIDEO_PLACEHOLDER_SRC
    : DEFAULT_PLACEHOLDER_SRC
  const resolvedFallback = fallbackSrc ?? defaultFallback
  const [failedPrimarySrc, setFailedPrimarySrc] = useState<string | null>(null)
  const isUsingFallback = !normalizedSrc || failedPrimarySrc === normalizedSrc
  const currentSrc = isUsingFallback ? resolvedFallback : normalizedSrc
  const imageRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    const image = imageRef.current

    if (!image || isUsingFallback || !normalizedSrc) return
    if (image.complete && image.naturalWidth === 0) {
      setFailedPrimarySrc(normalizedSrc)
    }
  }, [isUsingFallback, normalizedSrc])

  return (
    <img
      {...imgProps}
      ref={imageRef}
      alt={alt}
      src={currentSrc}
      onError={event => {
        onError?.(event)

        if (isUsingFallback) return
        setFailedPrimarySrc(normalizedSrc)
      }}
    />
  )
}
